import PropTypes from "prop-types"
import { capitalize } from "lodash"
import { DESC } from "helpers/sorts"
import { ATTRIBUTION_METHODS } from "helpers/risk"
import { useAssetViewDashboardMetrics } from "helpers/metrics/hooks"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import ContributorsTable from "renderers/contributors-datagrid"
import { CLASSIFICATIONS_SHAPE } from "helpers/shapes/asset-view"
import { useAssetViewTemplateChart } from "../../hooks/use-asset-view-template-chart"
import { ASSET_VIEW_RISK_CHART } from "../template-chart-config"
import { useAnalytics } from "connected-components/analytics"
import { useAssetViewData } from "connected-components/asset-view/hooks/use-asset-view-data"
import { useAssetViewHeaderHandlers } from "../../hooks/use-asset-view-header-handlers"

export default function AssetViewRisk({ modelMetadata, instrumentsWithMetadata, highlightedRowIds, disableActiveTrading, instrumentColumns, classifications }) {
  const { selectedCategory } = useAssetViewTemplateChart(ASSET_VIEW_RISK_CHART)
  const contextData = useAssetViewData()
  const {
    instrument: original,
    modelId,
    base: contextBase,
    groupBy,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    setTradedPosition,
    selectedTableLevels,
    setSelectedTableLevels,
    isExpanded,
    isEndVsStartDateComparison,
  } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useAssetViewHeaderHandlers({ track: analytics.track })

  const attributionMethod = contextBase ? ATTRIBUTION_METHODS.MCTE : ATTRIBUTION_METHODS.MCAR

  const gatherers = useTemplateGatherers(contextData, "risk", {
    selectedCategory,
    attributionMethod,
    classifications,
  })

  const metrics = useAssetViewDashboardMetrics(
    [{ category: "risk", selectedCategory }],
    original,
    rebalanced,
    comparison,
    { instrumentColumns, isActive: !!contextBase, modelMetadata, instrumentsWithMetadata, setTradedPosition, isExpanded, disableActiveTrading, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    modelId={modelId}
    mainInstrument={original}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
        highlightedRowIds,
        groupByName: groupBy?.label,
        defaultSort: {
          path: `marketValue${capitalize(defaultSortSuffix)}`,
          direction: DESC,
        },
        downloadKey: "assets.risk",
        downloadName: "Assets - Risk",
        tableNavigationLevels: selectedTableLevels,
        onTableNavigationChange: setSelectedTableLevels,
        // Analytics callbacks
        onSortChanged: handleSortChanged,
        onTableNavigationChanged: handleTableNavigationChanged,
    }}
  />
}

AssetViewRisk.propTypes = {
  modelMetadata: PropTypes.object.isRequired,
  instrumentsWithMetadata: PropTypes.object.isRequired,
  highlightedRowIds: PropTypes.arrayOf(PropTypes.string),
  disableActiveTrading: PropTypes.bool.isRequired,
  instrumentColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  classifications: CLASSIFICATIONS_SHAPE.isRequired,
}
